import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../cn';

/**
 * Badge — role/status/billable pills (DESIGN.md §5 "Chips"). Semantic
 * variants use a subtle tint background with full-saturation text of the
 * same hue, never gray-on-gray for a status that carries real meaning.
 * Always pair with an icon or text label when used to convey status —
 * never rely on color alone (PRODUCT.md accessibility principle).
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-label font-medium leading-none',
  {
    variants: {
      variant: {
        neutral: 'border-border bg-surface text-ink',
        gold: 'border-transparent bg-gold-subtle text-gold-deep',
        teal: 'border-transparent bg-teal/10 text-teal',
        success: 'border-transparent bg-success/15 text-success',
        warning: 'border-transparent bg-warning/15 text-warning',
        danger: 'border-transparent bg-danger/15 text-danger',
        info: 'border-transparent bg-info/15 text-info',
      },
    },
    defaultVariants: { variant: 'neutral' },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export function Badge({ className, variant, ...props }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { badgeVariants };
